import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Lock, ShieldCheck, Loader2, CheckCircle2, XCircle } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";

const Shell = ({ title, subtitle, children }) => {
  const { theme, toggleTheme } = useApp();
  return (
    <div className="min-h-screen grain flex flex-col items-center justify-center px-4 py-10">
      <button data-testid="auth-theme-toggle" onClick={toggleTheme}
              className="absolute top-6 right-6 text-xs text-muted-foreground hover:text-foreground">
        {theme === "dark" ? "☀ Light" : "☾ Dark"}
      </button>
      <div className="w-full max-w-md rounded-xl border border-border bg-card p-8">
        <div className="flex items-center gap-2 text-primary mb-6">
          <ShieldCheck className="w-5 h-5" />
          <span className="text-xs font-bold tracking-[0.2em] uppercase">SkillBridge</span>
        </div>
        <h1 className="font-heading text-2xl sm:text-3xl font-black tracking-tight">{title}</h1>
        {subtitle && <p className="text-muted-foreground mt-1">{subtitle}</p>}
        <div className="mt-6">{children}</div>
      </div>
    </div>
  );
};

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [devLink, setDevLink] = useState("");
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setSent(true);
      if (data?._dev_reset_token) {
        const link = `/reset-password?token=${data._dev_reset_token}`;
        setDevLink(link);
      }
    } catch {
      toast.error("Something went wrong. Please try again.");
    }
    setBusy(false);
  };

  return (
    <Shell title="Forgot your password?" subtitle="We'll email you a secure link to reset it.">
      {!sent ? (
        <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
          <div>
            <Label className="text-xs font-bold tracking-[0.15em] uppercase text-muted-foreground flex items-center gap-1.5 mb-1.5"><Mail className="w-4 h-4" /> Email</Label>
            <Input data-testid="forgot-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />
          </div>
          <Button data-testid="forgot-submit" type="submit" disabled={busy} className="w-full">
            {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null} Send reset link
          </Button>
          <div className="text-center text-sm">
            <Link to="/login" className="text-primary font-semibold hover:underline">Back to sign in</Link>
          </div>
        </form>
      ) : (
        <div className="space-y-4" data-testid="forgot-sent">
          <div className="rounded-lg bg-primary/10 text-primary p-4 flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 mt-0.5 shrink-0" />
            <div className="text-sm">
              If an account exists for <b>{email}</b>, a password-reset link has been sent. The link expires in 30 minutes.
            </div>
          </div>
          {devLink && (
            <div className="rounded-lg border border-dashed border-border p-4 text-sm">
              <div className="text-xs font-bold tracking-[0.15em] uppercase text-muted-foreground mb-1">Demo mode</div>
              <p className="text-muted-foreground">Email is not configured on this environment. Open your reset link here:</p>
              <Button data-testid="forgot-dev-link" className="mt-3 w-full" onClick={() => navigate(devLink)}>Open reset link</Button>
            </div>
          )}
          <div className="text-center text-sm">
            <Link to="/login" className="text-primary font-semibold hover:underline">Back to sign in</Link>
          </div>
        </div>
      )}
    </Shell>
  );
}

export function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  const navigate = useNavigate();
  const { refreshUser } = useApp();

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password.length < 6) return setErr("Password must be at least 6 characters");
    if (password !== confirm) return setErr("Passwords do not match");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      const u = await refreshUser();
      setDone(true);
      toast.success("Password reset. Signed in.");
      setTimeout(() => navigate(u?.role === "employer" ? "/employer" : "/worker", { replace: true }), 900);
    } catch (ex) {
      setErr(ex.response?.data?.detail || "Reset failed");
    }
    setBusy(false);
  };

  if (!token) {
    return (
      <Shell title="Missing reset token">
        <p className="text-muted-foreground">Please use the link from your reset email.</p>
      </Shell>
    );
  }

  return (
    <Shell title="Choose a new password" subtitle="Make it at least 6 characters.">
      {done ? (
        <div className="rounded-lg bg-primary/10 text-primary p-4 flex items-start gap-3">
          <CheckCircle2 className="w-5 h-5 mt-0.5" /><div className="text-sm">Password reset! Signing you in…</div>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
          <div>
            <Label className="text-xs font-bold tracking-[0.15em] uppercase text-muted-foreground flex items-center gap-1.5 mb-1.5"><Lock className="w-4 h-4" /> New password</Label>
            <Input data-testid="reset-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Min 6 characters" />
          </div>
          <div>
            <Label className="text-xs font-bold tracking-[0.15em] uppercase text-muted-foreground flex items-center gap-1.5 mb-1.5"><Lock className="w-4 h-4" /> Confirm password</Label>
            <Input data-testid="reset-confirm" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} />
          </div>
          {err && <div data-testid="reset-error" className="text-sm text-destructive">{err}</div>}
          <Button data-testid="reset-submit" type="submit" disabled={busy} className="w-full">
            {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null} Set new password
          </Button>
        </form>
      )}
    </Shell>
  );
}

export function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [state, setState] = useState("verifying"); // verifying | ok | error
  const [msg, setMsg] = useState("");
  const { refreshUser } = useApp();
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) { setState("error"); setMsg("Missing verification token"); return; }
    (async () => {
      try {
        await api.post("/auth/verify-email", { token });
        await refreshUser();
        setState("ok");
      } catch (e) {
        setState("error");
        setMsg(e.response?.data?.detail || "Invalid or expired link");
      }
    })();
    // eslint-disable-next-line
  }, []);

  return (
    <Shell title="Email verification">
      {state === "verifying" && (
        <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Verifying your email…</div>
      )}
      {state === "ok" && (
        <div data-testid="verify-email-ok" className="rounded-lg bg-primary/10 text-primary p-4 flex items-start gap-3">
          <CheckCircle2 className="w-5 h-5 mt-0.5" />
          <div>
            <div className="font-semibold">You're verified!</div>
            <p className="text-sm text-muted-foreground mt-1">Your SkillBridge email is confirmed.</p>
            <Button className="mt-3" size="sm" onClick={() => navigate("/")}>Continue</Button>
          </div>
        </div>
      )}
      {state === "error" && (
        <div data-testid="verify-email-error" className="rounded-lg bg-destructive/10 text-destructive p-4 flex items-start gap-3">
          <XCircle className="w-5 h-5 mt-0.5" />
          <div>
            <div className="font-semibold">Verification failed</div>
            <p className="text-sm mt-1">{msg}</p>
            <Button className="mt-3" size="sm" variant="outline" onClick={() => navigate("/account")}>Resend link</Button>
          </div>
        </div>
      )}
    </Shell>
  );
}
